import React, { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import api from '../../../lib/api';
import Button from '../../Button';
import { OpenExternalIcon, RefreshIcon } from '../../icons';
import { DisclosureRow } from './detailAtoms';

interface AdminSourceChangeWire {
  id: string;
  changed_at: string;
  source_code: string;
  field_path: string;
  change_type: string;
  before_value: unknown;
  after_value: unknown;
  projection_disposition: string;
  final_url: string | null;
  source_url: string | null;
  cause?: string | null;
  operation?: string | null;
  mode?: string | null;
}

interface PublicSourceUpdateWire {
  occurredAt: string;
  changeType: string;
  field: string;
  fieldLabel: string;
  beforeValue: string | number | boolean | null;
  afterValue: string | number | boolean | null;
  sourceName: string;
  sourceUrl: string | null;
}

interface ChangePageWire<T> {
  entries: T[];
  next_cursor: string | null;
  has_more: boolean;
  history_state?: 'changes_available' | 'checked_no_changes' | 'never_checked';
  last_successful_check_at?: string | null;
}

interface SourceUpdate {
  key: string;
  occurredAt: string;
  changeType: string;
  fieldLabel: string;
  beforeValue: unknown;
  afterValue: unknown;
  sourceName: string;
  sourceUrl: string | null;
  disposition: string | null;
  cause: string | null;
  operation: string | null;
  mode: string | null;
}

interface OfficialSourceUpdatesProps {
  initiativeId: string;
  isPublic?: boolean;
  /**
   * Which audience's feed to fetch:
   * - 'public' — anonymous endpoint, ballot-visible measures only
   * - 'pro'    — authed endpoint, same curated feed without the visibility gate
   * - 'admin'  — admin source-changes ledger with source/disposition filters
   * Defaults from isPublic for the two original callers.
   */
  variant?: 'public' | 'pro' | 'admin';
  /**
   * 'card' (default) is a standalone card with its own heading. 'disclosure' is
   * one collapsed row of the measure detail's Official record, its sub-label
   * summarising what the feed holds.
   */
  layout?: 'card' | 'disclosure';
}

const SOURCE_NAMES: Record<string, string> = {
  colorado_sos_title_board: 'Colorado Secretary of State Title Board',
  colorado_legislative_council_initiatives: 'Colorado Legislative Council',
};

const FIELD_LABELS: Record<string, string> = {
  source_presence: 'Source listing',
  status: 'Status',
  exact_status_text: 'Official status text',
  exact_title: 'Official title',
  ballot_title: 'Official ballot title',
  exact_caption: 'Unofficial caption',
  unofficial_caption: 'Unofficial caption',
  description: 'SOS full title',
  measure_type: 'Measure type',
  approval_threshold_pct: 'Approval threshold',
  is_single_subject_approved: 'Single-subject decision',
  petition_deadline: 'Petition deadline',
  full_text_url: 'Measure text link',
  fiscal_impact_url: 'Fiscal impact link',
  lifecycle_events: 'Official lifecycle',
  designated_representatives: 'Designated representatives',
  filing_links: 'Official filing links',
  document_links: 'Official document links',
};

/** "Oct 2, 2026, 10:18 PM" — a check time in the reader's own timezone. */
const formatCheckedAt = (value: string): string =>
  new Date(value).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

/**
 * The disclosure row's sub-label: what is inside before the reader opens it.
 * Exported for tests.
 */
export function sourceUpdatesSummary(state: {
  isLoading: boolean;
  isError: boolean;
  count: number;
  hasMore: boolean;
  historyState?: ChangePageWire<unknown>['history_state'];
  lastCheckedAt?: string | null;
}): string {
  if (state.isLoading) return 'Loading…';
  if (state.isError) return 'Temporarily unavailable';
  const checked = state.lastCheckedAt ? ` · last checked ${formatCheckedAt(state.lastCheckedAt)}` : '';
  if (state.count > 0) {
    const count = state.hasMore ? `${state.count}+ updates` : `${state.count} update${state.count === 1 ? '' : 's'}`;
    return `${count} recorded${checked}`;
  }
  if (state.historyState === 'never_checked') return 'Not yet checked';
  return `No changes found${checked}`;
}

const humanize = (value: string): string => (
  value.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase())
);

export const dispositionLabel = (value: string): string => ({
  applied: 'Applied to initiative',
  sos_precedence_suppressed: 'Not applied because SOS evidence takes precedence',
  human_owned_suppressed: 'Not applied because an administrator manages this field',
  observation_only: 'Retained as source evidence only',
}[value] ?? humanize(value));

export const displayValue = (value: unknown): string => {
  if (value == null || value === '') return 'Not present';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return `${value.length} source item${value.length === 1 ? '' : 's'}`;
  return 'Updated';
};

export const normalizeAdmin = (entry: AdminSourceChangeWire): SourceUpdate => ({
  key: entry.id,
  occurredAt: entry.changed_at,
  changeType: entry.change_type,
  fieldLabel: FIELD_LABELS[entry.field_path] || humanize(entry.field_path),
  beforeValue: entry.before_value,
  afterValue: entry.after_value,
  sourceName: SOURCE_NAMES[entry.source_code] || 'Official Colorado source',
  sourceUrl: entry.final_url || entry.source_url,
  disposition: entry.projection_disposition,
  cause: entry.cause ?? null,
  operation: entry.operation ?? null,
  mode: entry.mode ?? null,
});

export const normalizePublic = (entry: PublicSourceUpdateWire, index: number): SourceUpdate => ({
  key: `${entry.occurredAt}-${entry.field}-${entry.changeType}-${index}`,
  occurredAt: entry.occurredAt,
  changeType: entry.changeType,
  fieldLabel: entry.fieldLabel,
  beforeValue: entry.beforeValue,
  afterValue: entry.afterValue,
  sourceName: entry.sourceName,
  sourceUrl: entry.sourceUrl,
  disposition: null,
  cause: null,
  operation: null,
  mode: null,
});

const OfficialSourceUpdates: React.FC<OfficialSourceUpdatesProps> = ({
  initiativeId,
  isPublic = false,
  variant,
  layout = 'card',
}) => {
  const mode = variant ?? (isPublic ? 'public' : 'admin');
  // The public and pro feeds share the curated serialization and presentation;
  // only the endpoint (and its gate) differs. Admin keeps the raw ledger.
  const isCurated = mode !== 'admin';
  const [source, setSource] = useState('');
  const [disposition, setDisposition] = useState('');
  const query = useInfiniteQuery(
    ['ballot-initiative-source-updates', initiativeId, mode, source, disposition],
    async ({ pageParam = null }) => {
      if (isCurated) {
        const params = { limit: 10, cursor: pageParam || undefined };
        const response = mode === 'public'
          ? await axios.get<{ data: ChangePageWire<PublicSourceUpdateWire> }>(
              `/api/ballot-initiatives/public/${initiativeId}/source-updates`,
              { params }
            )
          : await api.get<{ data: ChangePageWire<PublicSourceUpdateWire> }>(
              `/ballot-initiatives/${initiativeId}/source-updates`,
              { params }
            );
        return {
          entries: response.data.data.entries.map(normalizePublic),
          nextCursor: response.data.data.next_cursor,
          hasMore: response.data.data.has_more,
          historyState: response.data.data.history_state,
          lastSuccessfulCheckAt: response.data.data.last_successful_check_at ?? null,
        };
      }
      const response = await api.get<{ data: ChangePageWire<AdminSourceChangeWire> }>(
        `/admin/ballot-initiatives/${initiativeId}/source-changes`,
        {
          params: {
            limit: 25,
            cursor: pageParam || undefined,
            source: source || undefined,
            disposition: disposition || undefined,
          },
        }
      );
      return {
        entries: response.data.data.entries.map(normalizeAdmin),
        nextCursor: response.data.data.next_cursor,
        hasMore: response.data.data.has_more,
        historyState: undefined,
        lastSuccessfulCheckAt: null,
      };
    },
    {
      getNextPageParam: lastPage => lastPage.nextCursor || undefined,
      staleTime: 30_000,
    }
  );

  const entries = query.data?.pages.flatMap(page => page.entries) || [];
  const publicState = query.data?.pages[0]?.historyState;
  const lastCheckedAt = query.data?.pages[0]?.lastSuccessfulCheckAt;
  const titleId = `${mode}-official-source-updates`;

  const body = (
    <>
      {query.isLoading ? (
        <div className="flex justify-center py-6"><LoadingSpinner size="md" /></div>
      ) : query.isError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
          <p>{isCurated ? 'Official source updates are temporarily unavailable.' : 'Source changes could not be loaded.'}</p>
          <Button variant="secondary" size="sm" icon={RefreshIcon} onClick={() => query.refetch()} className="mt-3">
            Retry
          </Button>
        </div>
      ) : entries.length === 0 ? (
        <div className="py-4 text-sm text-text-tertiary dark:text-text-dark-tertiary">
          <p>{isCurated && publicState === 'never_checked'
            ? 'No successful official-source check has been recorded.'
            : isCurated && publicState === 'checked_no_changes'
              ? 'Official sources were checked successfully; no public material changes were found.'
              : 'No material source changes have been recorded.'}</p>
          {isCurated && lastCheckedAt && <p className="mt-1 text-xs">Last successful check: {new Date(lastCheckedAt).toLocaleString()}</p>}
        </div>
      ) : (
        <>
          <ol className="divide-y divide-border-light dark:divide-border-dark">
            {entries.map(entry => (
              <li key={entry.key} className="py-4 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    {entry.fieldLabel} {humanize(entry.changeType).toLowerCase()}
                  </p>
                  <time className="text-xs text-text-tertiary dark:text-text-dark-tertiary" dateTime={entry.occurredAt}>
                    {new Date(entry.occurredAt).toLocaleString()}
                  </time>
                </div>
                <p className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary">
                  {displayValue(entry.beforeValue)} → {displayValue(entry.afterValue)}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {entry.sourceUrl ? (
                    <a
                      href={entry.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary-600 hover:underline dark:text-primary-400"
                    >
                      {entry.sourceName}<OpenExternalIcon className="h-3 w-3" aria-hidden="true" />
                    </a>
                  ) : entry.sourceName}
                  {entry.disposition && <span>· {dispositionLabel(entry.disposition)}</span>}
                  {entry.operation && <span>· {humanize(entry.operation)}</span>}
                  {entry.mode && <span>· {entry.mode}</span>}
                </div>
                {entry.cause && <p className="mt-2 text-xs text-text-secondary dark:text-text-dark-secondary">Reason: {humanize(entry.cause)}</p>}
              </li>
            ))}
          </ol>
          {query.hasNextPage && (
            <div className="mt-4 flex justify-center" aria-live="polite">
              <Button
                variant="secondary"
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => query.fetchNextPage()}
              >
                Load older updates
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );

  if (layout === 'disclosure') {
    return (
      <DisclosureRow
        title="Official source updates"
        subLabel={sourceUpdatesSummary({
          isLoading: query.isLoading,
          isError: query.isError,
          count: entries.length,
          hasMore: Boolean(query.hasNextPage),
          historyState: publicState,
          lastCheckedAt: lastCheckedAt ?? null,
        })}
      >
        <div aria-busy={query.isFetching}>
          <p className="mb-3 text-sm text-text-secondary dark:text-text-dark-secondary">
            Changes observed in official Colorado ballot-initiative sources.
          </p>
          {body}
        </div>
      </DisclosureRow>
    );
  }

  return (
    <section className="card p-5" aria-labelledby={titleId} aria-busy={query.isFetching}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={titleId} className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
            {isCurated ? 'Official Source Updates' : 'Source Changes'}
          </h2>
          <p className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            {isCurated
              ? 'Changes observed in official Colorado ballot-initiative sources.'
              : 'Append-only differences between successful source observations.'}
          </p>
        </div>
        {!isCurated && (
          <div className="flex flex-wrap gap-2">
            <label className="text-xs text-text-secondary dark:text-text-dark-secondary">
              <span className="sr-only">Filter by source</span>
              <select
                value={source}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setSource(event.target.value)}
                className="input text-xs py-1.5"
              >
                <option value="">All sources</option>
                <option value="colorado_sos_title_board">Secretary of State</option>
                <option value="colorado_legislative_council_initiatives">Legislative Council</option>
              </select>
            </label>
            <label className="text-xs text-text-secondary dark:text-text-dark-secondary">
              <span className="sr-only">Filter by projection disposition</span>
              <select
                value={disposition}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setDisposition(event.target.value)}
                className="input text-xs py-1.5"
              >
                <option value="">All dispositions</option>
                <option value="applied">Applied</option>
                <option value="sos_precedence_suppressed">SOS precedence</option>
                <option value="human_owned_suppressed">Human-owned value preserved</option>
                <option value="observation_only">Observation only</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {body}
    </section>
  );
};

export default OfficialSourceUpdates;
